import type {
  SessionNoteDraft,
  SessionNoteDraftTreatment,
} from '@/api/assistantVoice/assistantVoiceApi.types';
import type { DeviceBrief, Treatment } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import {
  DURATION_UNITS,
  parseDeviceSettingDurationValue,
  serializeDeviceSettingDuration,
  type DurationUnit,
} from '@/utils/durationFormatting';
import type { DeviceSettingSet } from './deviceSettingSets';
import type { EditorTreatment } from './types';

export interface DraftableNoteFields {
  painScore: string;
  assessment: string;
  privateNotes: string;
  bodyAreaIds: string[];
  treatments: EditorTreatment[];
}

interface DraftApplicationContext {
  library: Treatment[];
  createTreatmentKey: () => string;
  localizedName: (nameEn: string, namePl: string) => string;
}

const TEXT_SEPARATOR = '\n\n';
const BARE_NUMBER_PATTERN = /^-?\d+(?:[.,]\d+)?$/;

function mergeText(current: string, drafted: string | null): string {
  if (drafted === null || drafted.trim().length === 0) {
    return current;
  }
  if (current.trim().length === 0) {
    return drafted;
  }
  return current + TEXT_SEPARATOR + drafted;
}

function durationUnitFor(unit: string): DurationUnit {
  const lowered = unit.toLowerCase();
  const match = DURATION_UNITS.find(function same(candidate) {
    return candidate === lowered;
  });
  return match === undefined ? 'min' : match;
}

export function editableDraftSettingValue(
  device: DeviceBrief | null,
  fieldId: string,
  value: string
): string {
  if (device === null) {
    return value;
  }
  const field = device.settingFields.find(function matches(candidate) {
    return candidate.id === fieldId;
  });
  if (field === undefined || field.fieldType !== 'duration') {
    return value;
  }
  const unit = durationUnitFor(field.unit);
  const trimmed = value.trim();
  if (BARE_NUMBER_PATTERN.test(trimmed)) {
    return serializeDeviceSettingDuration(Number(trimmed.replace(',', '.')), unit);
  }
  const parsed = parseDeviceSettingDurationValue(trimmed, unit);
  if (Number.isNaN(parsed.amount)) {
    return value;
  }
  return serializeDeviceSettingDuration(parsed.amount, parsed.unit);
}

function draftedSettings(
  device: DeviceBrief | null,
  draftTreatment: SessionNoteDraftTreatment
): DeviceSettingSet {
  const deviceSettings: DeviceSettingSet = {};
  for (const setting of draftTreatment.deviceSettings) {
    deviceSettings[setting.deviceSettingFieldId] = editableDraftSettingValue(
      device,
      setting.deviceSettingFieldId,
      setting.value
    );
  }
  return deviceSettings;
}

function lastIndexOfTreatment(treatments: EditorTreatment[], treatmentId: string): number {
  for (let index = treatments.length - 1; index >= 0; index -= 1) {
    const candidate = treatments[index];
    if (candidate !== undefined && candidate.treatmentId === treatmentId) {
      return index;
    }
  }
  return -1;
}

function mergeIntoFirstSet(
  sets: DeviceSettingSet[],
  draftedSet: DeviceSettingSet
): DeviceSettingSet[] {
  const [firstSet, ...otherSets] = sets;
  return [{ ...(firstSet === undefined ? {} : firstSet), ...draftedSet }, ...otherSets];
}

function correctedTreatment(
  existing: EditorTreatment,
  draftTreatment: SessionNoteDraftTreatment
): EditorTreatment {
  return {
    ...existing,
    bodyAreaId:
      draftTreatment.bodyAreaId === null ? existing.bodyAreaId : draftTreatment.bodyAreaId,
    outcome: draftTreatment.outcome,
    response: draftTreatment.response === null ? existing.response : draftTreatment.response,
    deviceSettingSets: mergeIntoFirstSet(
      existing.deviceSettingSets,
      draftedSettings(existing.device, draftTreatment)
    ),
  };
}

export function applySessionNoteDraft(
  current: DraftableNoteFields,
  draft: SessionNoteDraft,
  context: DraftApplicationContext
): DraftableNoteFields {
  const treatments = [...current.treatments];
  for (const draftTreatment of draft.treatments) {
    const library = context.library.find(function matches(candidate) {
      return candidate.id === draftTreatment.treatmentId;
    });
    if (library === undefined) {
      continue;
    }
    const existingIndex = draftTreatment.correctsEarlier
      ? lastIndexOfTreatment(treatments, library.id)
      : -1;
    const existing = treatments[existingIndex];
    if (existing !== undefined) {
      treatments[existingIndex] = correctedTreatment(existing, draftTreatment);
      continue;
    }
    treatments.push({
      key: context.createTreatmentKey(),
      treatmentId: library.id,
      treatmentName: context.localizedName(library.nameEn, library.namePl),
      contraindication: library.contraindication,
      device: library.device,
      bodyAreaId: draftTreatment.bodyAreaId === null ? '' : draftTreatment.bodyAreaId,
      outcome: draftTreatment.outcome,
      response: draftTreatment.response === null ? '' : draftTreatment.response,
      deviceSettingSets: [draftedSettings(library.device, draftTreatment)],
      recalledFromLast: false,
    });
  }
  const bodyAreaIds = [...current.bodyAreaIds];
  for (const bodyAreaId of draft.bodyAreaIds) {
    if (!bodyAreaIds.includes(bodyAreaId)) {
      bodyAreaIds.push(bodyAreaId);
    }
  }
  return {
    painScore: draft.painScore === null ? current.painScore : String(draft.painScore),
    assessment: mergeText(current.assessment, draft.assessment),
    privateNotes: mergeText(current.privateNotes, draft.privateNotes),
    bodyAreaIds,
    treatments,
  };
}
